import type { OrganizationState, State } from 'src/store/types';
import type {
  OrganizationPageViewModel,
  SingleEmployee,
} from 'src/pages/organization-page/organization-page-view-model';
import {
  constitutionalDocumentsNotDeleted,
  currentEmployeeUuid,
  key,
  activityCodesDisplayText,
  sectorOptions,
  employeesNotDeleted,
  activityCodes,
  writeAccess,
} from 'src/store/selectors';
import { LocalDate } from 'src/utilities/local-date';
import type { AbstractPageView } from 'src/pages/abstract-page-view';
import { getDocsForLinking } from 'src/models/internal-linking';
import { BASE_PATH } from 'src/store';
import type { ListSectionItemInput } from 'src/library/lists/listSectionItemInput';
import type { ListItemIcon } from 'src/library/lists/listItemIcon';
import { calculateUpdateStatus } from 'src/models/resolvers/calculate-update-status';
import { entityNameForLists } from 'src/utilities/text';
import { sortedByLabel } from 'src/store/utilities';
import type { NamedTemplateItem } from 'src/models/resolvers/named-template-item';

function getAccessible<T extends NamedTemplateItem>(
  g: T,
  currentEmployeeUuid: string | undefined,
  hasWriteAccess: boolean,
) {
  // If it's a suggestion (unchangedTemplate), require write access
  if (g.unchangedTemplate && !hasWriteAccess) {
    return false;
  }
  if (g.classification === undefined) {
    return true;
  }
  return g.classification === 'NONE' || (g.accessControl ?? []).includes(currentEmployeeUuid ?? '');
}

function toListSectionItemInputs<T extends NamedTemplateItem>(
  hrefPrefix: string,
  list: T[],
  today: LocalDate,
  currentEmployeeUuid: string | undefined,
  hasWriteAccess: boolean,
): ListSectionItemInput[] {
  const items: ListSectionItemInput[] = list.map((g) => {
    let href = hrefPrefix + g.uuid;
    if (!g.isConfirmedEntity || !g.name || g.unchangedTemplate) {
      href += '?edit';
    }
    let mainIcon: ListItemIcon | undefined = undefined;
    if (g.classification !== undefined && g.classification !== 'NONE') {
      mainIcon = 'lock';
    } else if (g.unchangedTemplate) {
      mainIcon = 'suggestion';
    }
    return {
      mainIcon,
      accessible: getAccessible(g, currentEmployeeUuid, hasWriteAccess),
      updateStatus: calculateUpdateStatus(g, today),
      label: entityNameForLists(g.name, g.draftName, g.isConfirmedEntity ?? true),
      secondaryLabel: '',
      href,
      hasDraft: g.hasDraft ?? false,
    };
  });
  const notSuggestions = sortedByLabel(
    items.filter((item) => {
      return item.mainIcon !== 'suggestion';
    }),
  );
  const suggestions = sortedByLabel(
    items.filter((item) => {
      return item.mainIcon === 'suggestion';
    }),
  );
  return notSuggestions.concat(suggestions);
}

function invoiceReceiverText(organization: OrganizationState | undefined): string {
  if (organization) {
    const parts = [
      organization.invoiceReceiver,
      organization.invoiceAddress,
      [organization.invoicePostcode, organization.invoiceLocality].filter(Boolean).join(' '),
    ].filter(Boolean);
    return parts.join('\n');
  }
  return '';
}

export function organizationPageView(
  hrefPrefix: string,
  viewModel: AbstractPageView,
  state: State,
): OrganizationPageViewModel {
  const o = state.organization;

  if (o === undefined) {
    throw new Error('Illegal state (E151), organization not found');
  }

  const nullToUndefined = (v: string | null) => {
    return v === null ? undefined : v;
  };

  const employee = employeesNotDeleted(state)[0];
  const singleEmployee: SingleEmployee = {
    name: employee.name,
    associationType: employee.associationType ?? '',
    profession: employee.profession ?? '',
    href: hrefPrefix + 'employees/' + employee.uuid,
  };

  return {
    ...viewModel,
    type: 'organization-page',
    icon: 'partners',
    href: hrefPrefix,
    availableActivityCodes: activityCodes(viewModel.sector),
    pdfHref: BASE_PATH + '/organizations/' + o.organizationId + '/overview.pdf?key=' + key(state),
    name: o.name,
    sectorDisplayText:
      sectorOptions.find((i) => {
        return i.value === o.sector;
      })?.text ?? '',
    activityCodes: o.activityCodes,
    activityCodesDisplayText: activityCodesDisplayText(activityCodes(viewModel.sector), o.activityCodes),
    url: nullToUndefined(o.url),
    notes: nullToUndefined(o.notes),
    email: nullToUndefined(o.email),
    fax: nullToUndefined(o.fax),
    phone: nullToUndefined(o.phone),
    mailAddress: nullToUndefined(o.mailAddress),
    officeAddress: nullToUndefined(o.officeAddress),
    invoiceReceiverText: invoiceReceiverText(state.organization),
    herNumber: nullToUndefined(o.herNumber),
    organizationNumber: nullToUndefined(o.organizationNumber),
    businessEntityType: o.businessEntityType,
    sector: viewModel.sector,
    singleEmployee,
    constitutionalDocuments: toListSectionItemInputs(
      hrefPrefix + 'constitutionalDocuments/',
      constitutionalDocumentsNotDeleted(state),
      LocalDate.fromString(state.today),
      currentEmployeeUuid(state),
      writeAccess(state),
    ),
    docsForLinking: getDocsForLinking(state),
  };
}
